import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Search, X, SlidersHorizontal, Plus, Heart, TrendingUp, TrendingDown, Minus, Loader2, ArrowUpDown } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { RarityBadge } from '@/components/RarityBadge';
import { CardSkeleton } from '@/components/CardSkeleton';
import { useCards, useCardSets, useCardPrints, useAllPrints, getBestPrintPrice, getCardPriceFromPrints, TREATMENT_LABELS, type Card, type CardPrint } from '@/hooks/useCards';
import { useAddToCollection, useCollection } from '@/hooks/useCollection';
import { useWishlist, useAddToWishlist, useRemoveFromWishlist } from '@/hooks/useWishlist';
import { useAuth } from '@/hooks/useAuth';
import { getCardPrice, formatPrice } from '@/lib/card-prices';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

const INK_COLORS = ['All', 'Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'] as const;
const RARITIES = ['All', 'Common', 'Uncommon', 'Rare', 'Super Rare', 'Legendary', 'Enchanted'] as const;
const CARD_TYPES = ['All', 'Character', 'Action', 'Item', 'Location', 'Song'] as const;
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'set', label: 'Set' },
  { value: 'price_desc', label: 'Price ↓' },
  { value: 'price_asc', label: 'Price ↑' },
  { value: 'rarity', label: 'Rarity' },
] as const;

const RARITY_ORDER: Record<string, number> = {
  'Enchanted': 7, 'Legendary': 6, 'Super Rare': 5, 'Epic': 4, 'Rare': 3, 'Uncommon': 2, 'Common': 1, 'Special': 4, 'Iconic': 5,
};

const PAGE_SIZE = 60;

function TreatmentSelector({ prints, selectedPrint, onSelect }: { prints: CardPrint[]; selectedPrint: CardPrint | null; onSelect: (p: CardPrint) => void }) {
  if (!prints.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Versions</p>
      <div className="flex flex-wrap gap-1.5">
        {prints.map(p => {
          const info = TREATMENT_LABELS[p.treatment] || { label: p.treatment, emoji: '🃏' };
          const isSelected = selectedPrint?.id === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-medium font-body transition-all border ${
                isSelected
                  ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                  : 'border-border bg-secondary text-secondary-foreground hover:border-primary/30'
              }`}
            >
              <span>{info.emoji}</span>
              <span>{info.label}</span>
              {p.price_usd != null && (
                <span className={`ml-1 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                  ${Number(p.price_usd).toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CardPriceDisplay({ prints, selectedPrint }: { prints: CardPrint[]; selectedPrint: CardPrint | null }) {
  const print = selectedPrint || prints.find(p => p.treatment === 'normal') || prints[0];
  if (!print) return null;

  const info = TREATMENT_LABELS[print.treatment] || { label: print.treatment, emoji: '🃏' };

  return (
    <div className="mt-4 rounded-xl border border-border bg-secondary p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-foreground font-body flex items-center gap-1.5">
          💰 {info.emoji} {info.label} Price
        </h4>
        <span className="text-[10px] text-muted-foreground font-body">
          {print.price_usd_updated ? `Updated ${new Date(print.price_usd_updated).toLocaleDateString()}` : 'Lorcast'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="text-center">
          <p className="text-[10px] text-muted-foreground font-body">Market</p>
          <p className="text-sm font-bold text-primary font-body">
            {print.price_usd != null ? `$${Number(print.price_usd).toFixed(2)}` : '—'}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-muted-foreground font-body">Foil</p>
          <p className="text-sm font-medium text-ink-amethyst font-body">
            {print.price_usd_foil != null ? `$${Number(print.price_usd_foil).toFixed(2)}` : '—'}
          </p>
        </div>
      </div>
      {/* All variants price summary */}
      {prints.length > 1 && (
        <div className="mt-2 pt-2 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {prints.filter(p => p.price_usd != null).map(p => {
              const pInfo = TREATMENT_LABELS[p.treatment] || { label: p.treatment, emoji: '🃏' };
              return (
                <span key={p.id} className="text-[10px] text-muted-foreground font-body">
                  {pInfo.emoji} ${Number(p.price_usd!).toFixed(2)}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function LibraryTab() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [inkFilter, setInkFilter] = useState<string>('All');
  const [rarityFilter, setRarityFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [setFilter, setSetFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('name');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [selectedPrint, setSelectedPrint] = useState<CardPrint | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data: cards, isLoading } = useCards({
    ink_color: inkFilter !== 'All' ? inkFilter : undefined,
    rarity: rarityFilter !== 'All' ? rarityFilter : undefined,
    set_name: setFilter !== 'All' ? setFilter : undefined,
    search: search || undefined,
  });

  const { data: allSets } = useCardSets();
  const { data: collection } = useCollection();
  const { data: wishlist } = useWishlist();
  const { data: prints } = useCardPrints(selectedCard?.id ?? null);
  const { data: allPrintsByCard } = useAllPrints();
  const addToCollection = useAddToCollection();
  const addToWishlist = useAddToWishlist();
  const removeFromWishlist = useRemoveFromWishlist();

  // Reset selected print when card changes
  useEffect(() => {
    setSelectedPrint(null);
  }, [selectedCard?.id]);

  // Real market price for a single card from its prints, with estimate fallback
  const getRealPrice = (card: Card): number => {
    const real = getBestPrintPrice(allPrintsByCard?.get(card.id));
    return real ?? getCardPrice(card).mid;
  };

  // Dedupe by normalized name+subtitle so the same card from different sets/printings
  // appears once. Track sibling cards so we can surface per-rarity prices.
  const normalizeKey = (c: Card) =>
    `${(c.name || '').toLowerCase().replace(/[’']/g, "'").trim()}|${(c.subtitle || '').toLowerCase().trim()}`;

  const grouped = useMemo(() => {
    let filtered = cards || [];
    if (typeFilter !== 'All') filtered = filtered.filter(c => c.card_type === typeFilter);

    const map = new Map<string, { rep: Card; siblings: Card[] }>();
    for (const c of filtered) {
      const key = normalizeKey(c);
      const entry = map.get(key);
      if (!entry) {
        map.set(key, { rep: c, siblings: [c] });
      } else {
        entry.siblings.push(c);
        if (getRealPrice(c) < getRealPrice(entry.rep)) entry.rep = c;
      }
    }
    return Array.from(map.values());
  }, [cards, typeFilter, allPrintsByCard]);

  // Per-rarity prices for a group. Returns the cheapest real-market price per rarity,
  // a price range across the entire group, and a flag indicating whether any prices
  // are estimates rather than live market data.
  const getGroupPrices = (g: { rep: Card; siblings: Card[] }) => {
    const byRarity = new Map<string, { price: number; estimated: boolean }>();
    let min = Infinity;
    let max = -Infinity;
    let anyEstimated = false;
    for (const s of g.siblings) {
      const real = getBestPrintPrice(allPrintsByCard?.get(s.id));
      const price = real ?? getCardPrice(s).mid;
      const estimated = real == null;
      if (estimated) anyEstimated = true;
      if (price < min) min = price;
      if (price > max) max = price;
      const prev = byRarity.get(s.rarity);
      if (!prev || price < prev.price) byRarity.set(s.rarity, { price, estimated });
    }
    if (!isFinite(min)) { min = 0; max = 0; }
    return { min, max, byRarity, anyEstimated };
  };


  const sorted = useMemo(() => {
    const arr = [...grouped];
    switch (sortBy) {
      case 'set':
        arr.sort((a, b) => a.rep.set_name.localeCompare(b.rep.set_name) || a.rep.name.localeCompare(b.rep.name));
        break;
      case 'price_desc':
        arr.sort((a, b) => getGroupPrices(b).max - getGroupPrices(a).max);
        break;
      case 'price_asc':
        arr.sort((a, b) => getGroupPrices(a).min - getGroupPrices(b).min);
        break;
      case 'rarity':
        arr.sort((a, b) => (RARITY_ORDER[b.rep.rarity] || 0) - (RARITY_ORDER[a.rep.rarity] || 0));
        break;
      default:
        arr.sort((a, b) => a.rep.name.localeCompare(b.rep.name));
    }
    return arr;
  }, [grouped, sortBy]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, inkFilter, rarityFilter, typeFilter, setFilter, sortBy]);

  const visibleCards = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
  const hasMore = visibleCount < sorted.length;

  // Infinite scroll observer
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(prev => Math.min(prev + PAGE_SIZE, sorted.length));
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, sorted.length]);

  const activeFilterCount = [inkFilter, rarityFilter, typeFilter, setFilter].filter(f => f !== 'All').length;
  const collectionCardIds = new Set((collection || []).map(c => c.card_id));
  const wishlistCardIds = new Set((wishlist || []).map(w => w.card_id));

  const getOwnedQty = (cardId: string) => {
    const item = (collection || []).find(c => c.card_id === cardId);
    return item ? (item.quantity || 1) : 0;
  };

  const handleAddToCollection = async (card: Card) => {
    if (!user) { toast.error('Sign in to add cards'); return; }
    try {
      await addToCollection.mutateAsync({ cardId: card.id });
      toast.success(`${card.name} added to collection! ✨`);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleToggleWishlist = async (card: Card) => {
    if (!user) { toast.error('Sign in to use wishlist'); return; }
    try {
      if (wishlistCardIds.has(card.id)) {
        await removeFromWishlist.mutateAsync(card.id);
        toast.success(`Removed from wishlist`);
      } else {
        await addToWishlist.mutateAsync({ cardId: card.id });
        toast.success(`${card.name} added to wishlist! ❤️`);
      }
    } catch (err: any) { toast.error(err.message); }
  };

  // Get display price for a card in the grid
  const getDisplayPrice = (card: Card) => {
    const fallback = getCardPrice(card);
    return formatPrice(fallback.mid);
  };

  return (
    <div className="px-4 pb-4">
      {/* Search */}
      <div className="flex gap-2 mt-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search all Lorcana cards..."
            aria-label="Search all Lorcana cards"
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          aria-label={showFilters ? 'Hide filters' : 'Show filters'}
          aria-expanded={showFilters}
          className={`relative flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${showFilters ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground'}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="mt-2 rounded-xl border border-border bg-card p-3 space-y-3 animate-fade-in">
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink Color</p>
            <div className="flex flex-wrap gap-1.5">
              {INK_COLORS.map(ink => (
                <button key={ink} onClick={() => setInkFilter(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${inkFilter === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {ink !== 'All' && <InkDot ink={ink as InkColor} size="sm" />}
                  {ink}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Rarity</p>
            <div className="flex flex-wrap gap-1.5">
              {RARITIES.map(r => (
                <button key={r} onClick={() => setRarityFilter(r)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${rarityFilter === r ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Card Type</p>
            <div className="flex flex-wrap gap-1.5">
              {CARD_TYPES.map(t => (
                <button key={t} onClick={() => setTypeFilter(t)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${typeFilter === t ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          {allSets && allSets.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Set</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                <button onClick={() => setSetFilter('All')} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${setFilter === 'All' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>All</button>
                {allSets.map(s => (
                  <button key={s} onClick={() => setSetFilter(s)} className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors truncate max-w-[120px] ${setFilter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Sort By</p>
            <div className="flex flex-wrap gap-1.5">
              {SORT_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => setSortBy(opt.value)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${sortBy === opt.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  {opt.value === sortBy && <ArrowUpDown className="h-2.5 w-2.5" />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={() => { setInkFilter('All'); setRarityFilter('All'); setTypeFilter('All'); setSetFilter('All'); }} className="text-xs text-primary font-body underline">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      <p className="mt-3 text-[10px] text-muted-foreground font-body">
        {isLoading ? 'Loading...' : `${sorted.length} card${sorted.length !== 1 ? 's' : ''} found${hasMore ? ` · showing ${visibleCount}` : ''}`}
      </p>

      {/* Card grid with infinite scroll */}
      {isLoading ? (
        <div className="mt-2 grid grid-cols-3 gap-2">{Array.from({ length: 9 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : sorted.length === 0 ? (
        <div className="mt-8 py-12 text-center">
          <p className="text-4xl mb-3">🔍</p>
          <p className="text-sm text-muted-foreground font-body">No cards match your search</p>
        </div>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {visibleCards.map(group => {
              const card = group.rep;
              const { min, max, byRarity } = getGroupPrices(group);
              const owned = group.siblings.some(s => collectionCardIds.has(s.id));
              const wishlisted = group.siblings.some(s => wishlistCardIds.has(s.id));
              const sameRarities = byRarity.size;
              const priceLabel = min === max
                ? formatPrice(min)
                : `${formatPrice(min)}–${formatPrice(max)}`;
              return (
                <div key={card.id} className={`rounded-xl border bg-card p-1.5 cursor-pointer hover:border-primary/30 transition-colors animate-fade-in ${owned ? 'border-primary/30' : 'border-border'}`} onClick={() => setSelectedCard(card)}>
                  <div className="relative">
                    <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} enchanted={card.enchanted || false} showGlow className="aspect-[2/3]" />
                    {owned && <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-ink-emerald text-[8px] text-white">✓</span>}
                    {wishlisted && <span className="absolute top-1 left-1 text-ink-ruby text-xs">❤️</span>}
                    {sameRarities > 1 && (
                      <span className="absolute bottom-1 right-1 rounded-full bg-background/80 px-1.5 py-0.5 text-[8px] font-medium text-primary font-body backdrop-blur-sm">
                        {sameRarities} rarities
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 px-0.5">
                    <div className="flex items-center gap-1">
                      <InkDot ink={card.ink_color as InkColor} size="sm" />
                      <span className="truncate text-[10px] font-medium text-foreground font-body">{card.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      {card.subtitle && <span className="text-[9px] text-muted-foreground font-body truncate">{card.subtitle}</span>}
                      <span className="text-[9px] font-medium text-primary font-body ml-auto whitespace-nowrap">{priceLabel}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="ml-2 text-xs text-muted-foreground font-body">Loading more cards...</span>
            </div>
          )}
        </>
      )}

      {/* Card detail modal with variant selector */}
      {selectedCard && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setSelectedCard(null)}>
          <div className="animate-slide-up w-full max-w-[430px] max-h-[85vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-foreground">{selectedCard.name}</h3>
              <button onClick={() => setSelectedCard(null)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex gap-4">
              <CardImage
                src={selectedPrint?.image_url || selectedCard.image_url}
                alt={selectedCard.name}
                inkColor={selectedCard.ink_color as InkColor}
                enchanted={selectedCard.enchanted || false}
                showGlow
                className="w-36 aspect-[2/3] flex-shrink-0"
              />
              <div className="space-y-2 min-w-0">
                {selectedCard.subtitle && <p className="text-sm text-muted-foreground font-body">{selectedCard.subtitle}</p>}
                <div className="flex items-center gap-2">
                  <InkDot ink={selectedCard.ink_color as InkColor} size="md" />
                  <span className="text-sm font-medium text-foreground font-body">{selectedCard.ink_color}</span>
                  <RarityBadge rarity={selectedPrint?.rarity || selectedCard.rarity} />
                </div>
                <p className="text-xs text-muted-foreground font-body">{selectedCard.set_name} {selectedCard.set_number && `· #${selectedCard.set_number}`}</p>
                <p className="text-xs text-muted-foreground font-body capitalize">{selectedCard.card_type}</p>
                <div className="grid grid-cols-2 gap-1.5 text-xs font-body">
                  {selectedCard.cost != null && <div className="rounded bg-secondary px-2 py-1"><span className="text-muted-foreground">Cost:</span> <span className="text-foreground font-medium">{selectedCard.cost}</span></div>}
                  {selectedCard.strength != null && <div className="rounded bg-secondary px-2 py-1"><span className="text-muted-foreground">STR:</span> <span className="text-foreground font-medium">{selectedCard.strength}</span></div>}
                  {selectedCard.willpower != null && <div className="rounded bg-secondary px-2 py-1"><span className="text-muted-foreground">WP:</span> <span className="text-foreground font-medium">{selectedCard.willpower}</span></div>}
                  {selectedCard.lore_value != null && <div className="rounded bg-secondary px-2 py-1"><span className="text-muted-foreground">Lore:</span> <span className="text-foreground font-medium">{selectedCard.lore_value}</span></div>}
                </div>
                {selectedCard.inkable != null && (
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${selectedCard.inkable ? 'bg-ink-sapphire/20 text-ink-sapphire' : 'bg-ink-ruby/20 text-ink-ruby'}`}>
                    {selectedCard.inkable ? 'Inkable' : 'Uninkable'}
                  </span>
                )}
              </div>
            </div>

            {/* Treatment/Variant selector */}
            {prints && prints.length > 0 && (
              <TreatmentSelector
                prints={prints}
                selectedPrint={selectedPrint}
                onSelect={setSelectedPrint}
              />
            )}

            {/* Price display from prints */}
            {prints && prints.length > 0 ? (
              <CardPriceDisplay prints={prints} selectedPrint={selectedPrint} />
            ) : (
              <div className="mt-4 rounded-xl border border-border bg-secondary p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-foreground font-body">💰 Est. Price</h4>
                  <span className="text-[10px] text-muted-foreground font-body">Estimated</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(() => {
                    const fp = getCardPrice(selectedCard);
                    return (
                      <>
                        <div className="text-center">
                          <p className="text-[10px] text-muted-foreground font-body">Low</p>
                          <p className="text-sm font-medium text-foreground font-body">{formatPrice(fp.low)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-muted-foreground font-body">Market</p>
                          <p className="text-sm font-bold text-primary font-body">{formatPrice(fp.mid)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-muted-foreground font-body">High</p>
                          <p className="text-sm font-medium text-foreground font-body">{formatPrice(fp.high)}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Per-rarity price breakdown across all printings of this card */}
            {(() => {
              const grp = grouped.find(g => normalizeKey(g.rep) === normalizeKey(selectedCard));
              if (!grp) return null;
              const { byRarity, anyEstimated } = getGroupPrices(grp);
              if (byRarity.size === 0) return null;
              const entries = Array.from(byRarity.entries()).sort(
                (a, b) => (RARITY_ORDER[b[0]] || 0) - (RARITY_ORDER[a[0]] || 0)
              );
              return (
                <div className="mt-4 rounded-xl border border-border bg-secondary p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-foreground font-body">Prices by rarity</h4>
                    <span className="text-[10px] text-muted-foreground font-body">
                      {anyEstimated ? 'Live + estimated' : 'Live market'}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {entries.map(([rarity, info]) => (
                      <div key={rarity} className="flex items-center justify-between">
                        <RarityBadge rarity={rarity} />
                        <span className="text-xs font-medium text-primary font-body">
                          {formatPrice(info.price)}
                          {info.estimated && <span className="text-muted-foreground ml-1">est.</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}



            {selectedCard.abilities && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground font-body mb-1">Abilities</p>
                <p className="text-sm text-foreground font-body leading-relaxed">{selectedCard.abilities}</p>
              </div>
            )}
            {selectedCard.flavor_text && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground font-body italic leading-relaxed">{selectedCard.flavor_text}</p>
              </div>
            )}

            {/* Action buttons */}
            {user && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleAddToCollection(selectedCard)}
                  disabled={addToCollection.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  {collectionCardIds.has(selectedCard.id)
                    ? (addToCollection.isPending ? 'Adding...' : `Add Another (${getOwnedQty(selectedCard.id)})`)
                    : (addToCollection.isPending ? 'Adding...' : 'Add to Collection')}
                </button>
                <button
                  onClick={() => handleToggleWishlist(selectedCard)}
                  disabled={addToWishlist.isPending || removeFromWishlist.isPending}
                  className={`flex h-12 w-12 items-center justify-center rounded-lg border transition-colors disabled:opacity-50 ${
                    wishlistCardIds.has(selectedCard.id)
                      ? 'border-ink-ruby bg-ink-ruby/10 text-ink-ruby'
                      : 'border-border bg-secondary text-muted-foreground hover:text-ink-ruby hover:border-ink-ruby/30'
                  }`}
                >
                  <Heart className={`h-5 w-5 ${wishlistCardIds.has(selectedCard.id) ? 'fill-current' : ''}`} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
